import { useId, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes, type TextareaHTMLAttributes } from 'react';
import { cn } from '@/lib/cn';

const control =
  'w-full rounded-control bg-surface px-3 text-sm text-ink ring-1 ring-input placeholder:text-subtle ' +
  'focus-visible:outline-2 focus-visible:outline-focus focus-visible:ring-brand disabled:opacity-60 aria-[invalid=true]:ring-st-danger';

interface FieldShell {
  label: ReactNode;
  hint?: ReactNode;
  error?: string | null;
  className?: string;
  optional?: boolean;
}

function Shell({ id, label, hint, error, className, optional, children }: FieldShell & { id: string; children: ReactNode }) {
  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      <label htmlFor={id} className="text-[13px] font-medium text-ink-2">
        {label}
        {optional && <span className="ml-1 font-normal text-muted">(optional)</span>}
      </label>
      {children}
      {error ? (
        <p id={`${id}-err`} className="text-[13px] text-st-danger">{error}</p>
      ) : hint ? (
        <p id={`${id}-hint`} className="text-[13px] text-muted">{hint}</p>
      ) : null}
    </div>
  );
}

export function TextField({ label, hint, error, className, optional, ...rest }: FieldShell & InputHTMLAttributes<HTMLInputElement>) {
  const id = useId();
  return (
    <Shell id={id} label={label} hint={hint} error={error} className={className} optional={optional}>
      <input id={id} aria-invalid={Boolean(error) || undefined} aria-describedby={error ? `${id}-err` : hint ? `${id}-hint` : undefined}
        className={cn(control, 'h-control')} {...rest} />
    </Shell>
  );
}

export function TextArea({ label, hint, error, className, optional, ...rest }: FieldShell & TextareaHTMLAttributes<HTMLTextAreaElement>) {
  const id = useId();
  return (
    <Shell id={id} label={label} hint={hint} error={error} className={className} optional={optional}>
      <textarea id={id} aria-invalid={Boolean(error) || undefined} className={cn(control, 'min-h-20 py-2')} {...rest} />
    </Shell>
  );
}

export function SelectField({ label, hint, error, className, optional, children, ...rest }: FieldShell & SelectHTMLAttributes<HTMLSelectElement>) {
  const id = useId();
  return (
    <Shell id={id} label={label} hint={hint} error={error} className={className} optional={optional}>
      <select id={id} aria-invalid={Boolean(error) || undefined} className={cn(control, 'h-control pr-8')} {...rest}>
        {children}
      </select>
    </Shell>
  );
}

export function Checkbox({ label, hint, className, ...rest }: { label: ReactNode; hint?: ReactNode; className?: string } & InputHTMLAttributes<HTMLInputElement>) {
  const id = useId();
  return (
    <div className={cn('flex items-start gap-2.5', className)}>
      <input id={id} type="checkbox" className="mt-0.5 size-4 accent-[var(--brand)]" {...rest} />
      <label htmlFor={id} className="text-sm text-ink">
        {label}
        {hint && <span className="block text-[13px] text-muted">{hint}</span>}
      </label>
    </div>
  );
}
